/**
 * DeliveriesSection Component
 * Dynamic delivery entries for daily logs
 * BuilderBel Construction Project Management System
 */

import { ChevronDown, ChevronRight, Package, Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { Control, Controller, FieldErrors, UseFieldArrayReturn } from 'react-hook-form';
import type { DeliveryCondition, DeliveryEntry } from '../../../types/dailyLog';
import { Dropdown, Input, NumberInput, TextArea, TimePicker } from '../../forms';

// Delivery condition options
const DELIVERY_CONDITION_OPTIONS: { value: DeliveryCondition; label: string }[] = [
  { value: 'good', label: 'Good Condition' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'partial', label: 'Partial Delivery' },
  { value: 'rejected', label: 'Rejected' },
];

const CONDITION_COLORS: Record<DeliveryCondition, string> = {
  good: 'bg-green-100 text-green-800',
  damaged: 'bg-red-100 text-red-800',
  partial: 'bg-yellow-100 text-yellow-800',
  rejected: 'bg-red-100 text-red-800',
};

// Common units
const UNIT_OPTIONS = [
  'each',
  'box',
  'pallet',
  'bundle',
  'roll',
  'bag',
  'ton',
  'cu yd',
  'sq ft',
  'lin ft',
  'gallon',
  'lot',
  'truckload',
];

interface DeliveriesSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldArray: UseFieldArrayReturn<any, 'deliveries', 'id'>;
  isExpanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

// Helper to format time for input
const formatTimeForInput = (isoString?: string): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

// Helper to create ISO string from date and time
const createISOFromTime = (baseDate: string, time: string): string => {
  if (!time) return '';
  try {
    const [hours, minutes] = time.split(':');
    const date = new Date(baseDate);
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return date.toISOString();
  } catch {
    return '';
  }
};

export const DeliveriesSection: React.FC<DeliveriesSectionProps> = ({
  control,
  errors,
  fieldArray,
  isExpanded,
  onToggle,
  disabled = false,
}) => {
  const { fields, append, remove } = fieldArray;
  const deliveryErrors = errors.deliveries as unknown as FieldErrors<DeliveryEntry>[] | undefined;

  const handleAddEntry = () => {
    const now = new Date().toISOString();
    append({
      vendor: '',
      materials: '',
      purchaseOrderNumber: '',
      quantity: 1,
      unit: 'each',
      receivedBy: '',
      receivedAt: now,
      condition: 'good',
      storageLocation: '',
      notes: '',
    });
  };

  // Count by condition
  const conditionCounts = fields.reduce(
    (acc, _, index) => {
      const condition =
        (control._formValues?.deliveries?.[index]?.condition as DeliveryCondition) || 'good';
      acc[condition] = (acc[condition] || 0) + 1;
      return acc;
    },
    {} as Record<DeliveryCondition, number>
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Package className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">Deliveries</span>
          <span className="text-sm text-gray-500">
            ({fields.length} {fields.length === 1 ? 'delivery' : 'deliveries'})
          </span>
        </div>
        <div className="flex items-center space-x-4">
          {fields.length > 0 && (
            <div className="flex items-center space-x-2">
              {conditionCounts.good > 0 && (
                <span className={`px-2 py-0.5 text-xs rounded ${CONDITION_COLORS.good}`}>
                  {conditionCounts.good} good
                </span>
              )}
              {conditionCounts.damaged > 0 && (
                <span className={`px-2 py-0.5 text-xs rounded ${CONDITION_COLORS.damaged}`}>
                  {conditionCounts.damaged} damaged
                </span>
              )}
              {conditionCounts.partial > 0 && (
                <span className={`px-2 py-0.5 text-xs rounded ${CONDITION_COLORS.partial}`}>
                  {conditionCounts.partial} partial
                </span>
              )}
            </div>
          )}
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {/* Entries */}
          {fields.map((field, index) => {
            const condition = control._formValues?.deliveries?.[index]
              ?.condition as DeliveryCondition;

            return (
              <div
                key={field.id}
                className={`p-4 border rounded-lg space-y-4 ${
                  condition === 'good'
                    ? 'border-gray-200 bg-gray-50'
                    : condition === 'damaged' || condition === 'rejected'
                      ? 'border-red-200 bg-red-50/50'
                      : 'border-yellow-200 bg-yellow-50/50'
                }`}
              >
                {/* Entry Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-700">Delivery #{index + 1}</span>
                    {condition && (
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${CONDITION_COLORS[condition]}`}
                      >
                        {DELIVERY_CONDITION_OPTIONS.find(o => o.value === condition)?.label}
                      </span>
                    )}
                  </div>
                  {!disabled && fields.length > 0 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                      title="Remove entry"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Vendor & Materials */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name={`deliveries.${index}.vendor`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Input
                        label="Vendor/Supplier"
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={deliveryErrors?.[index]?.vendor?.message}
                        disabled={disabled}
                        required
                        placeholder="Vendor name"
                      />
                    )}
                  />

                  <Controller
                    name={`deliveries.${index}.materials`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Input
                        label="Materials Delivered"
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={deliveryErrors?.[index]?.materials?.message}
                        disabled={disabled}
                        required
                        placeholder="Description of materials"
                      />
                    )}
                  />
                </div>

                {/* PO Number, Quantity, Unit, Condition */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Controller
                    name={`deliveries.${index}.purchaseOrderNumber`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Input
                        label="PO Number"
                        value={inputField.value || ''}
                        onChange={inputField.onChange}
                        error={deliveryErrors?.[index]?.purchaseOrderNumber?.message}
                        disabled={disabled}
                        placeholder="PO-001"
                      />
                    )}
                  />

                  <Controller
                    name={`deliveries.${index}.quantity`}
                    control={control}
                    render={({ field: inputField }) => (
                      <NumberInput
                        label="Quantity"
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={deliveryErrors?.[index]?.quantity?.message}
                        disabled={disabled}
                        min={0}
                        step={1}
                      />
                    )}
                  />

                  <Controller
                    name={`deliveries.${index}.unit`}
                    control={control}
                    render={({ field: inputField }) => (
                      <div>
                        <Input
                          label="Unit"
                          value={inputField.value || ''}
                          onChange={inputField.onChange}
                          error={deliveryErrors?.[index]?.unit?.message}
                          disabled={disabled}
                          placeholder="Select unit"
                          list={`unit-options-${index}`}
                        />
                        <datalist id={`unit-options-${index}`}>
                          {UNIT_OPTIONS.map(unit => (
                            <option key={unit} value={unit} />
                          ))}
                        </datalist>
                      </div>
                    )}
                  />

                  <Controller
                    name={`deliveries.${index}.condition`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Dropdown
                        label="Condition"
                        options={DELIVERY_CONDITION_OPTIONS}
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={deliveryErrors?.[index]?.condition?.message}
                        disabled={disabled}
                        required
                      />
                    )}
                  />
                </div>

                {/* Received By, Time, Storage Location */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Controller
                    name={`deliveries.${index}.receivedBy`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Input
                        label="Received By"
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={deliveryErrors?.[index]?.receivedBy?.message}
                        disabled={disabled}
                        required
                        placeholder="Name of receiver"
                      />
                    )}
                  />

                  <Controller
                    name={`deliveries.${index}.receivedAt`}
                    control={control}
                    render={({ field: inputField }) => (
                      <TimePicker
                        label="Time Received"
                        required
                        value={formatTimeForInput(inputField.value)}
                        onChange={val => {
                          const logDate =
                            control._formValues?.logDate ||
                            new Date().toISOString().split('T')[0];
                          inputField.onChange(createISOFromTime(logDate, val));
                        }}
                        disabled={disabled}
                      />
                    )}
                  />

                  <Controller
                    name={`deliveries.${index}.storageLocation`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Input
                        label="Storage Location"
                        value={inputField.value || ''}
                        onChange={inputField.onChange}
                        error={deliveryErrors?.[index]?.storageLocation?.message}
                        disabled={disabled}
                        placeholder="Where stored on site"
                      />
                    )}
                  />
                </div>

                {/* Notes */}
                <Controller
                  name={`deliveries.${index}.notes`}
                  control={control}
                  render={({ field: inputField }) => (
                    <TextArea
                      label="Notes"
                      value={inputField.value || ''}
                      onChange={inputField.onChange}
                      error={deliveryErrors?.[index]?.notes?.message}
                      disabled={disabled}
                      rows={2}
                      placeholder="Damage details, discrepancies, photos taken..."
                    />
                  )}
                />
              </div>
            );
          })}

          {/* Add Entry Button */}
          {!disabled && (
            <button
              type="button"
              onClick={handleAddEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-brand-500 hover:text-brand-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Log Delivery</span>
            </button>
          )}

          {/* Empty State */}
          {fields.length === 0 && disabled && (
            <div className="text-center py-6 text-gray-500">No deliveries logged</div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeliveriesSection;
